// АНИМ.1: drop-in замена useNavigate с глитч-переходом между экранами.
//
// Использование:
//
//   const tx = useScreenTransition();
//   tx.navigate('/battle', { variant: 'heavy_glitch' });
//
// Если useAnimationsEnabled() === false — сводится к мгновенной навигации,
// никакие фазы не запускаются (игрок видит экран сразу).
//
// Variant по умолчанию вычисляется через routeVariant(to). Можно
// переопределить через opts.variant.

import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { useAnimationsEnabled } from '../hooks/useAnimationsEnabled';
import { useTransitionStore } from './transitionStore';
import { VARIANT_TIMINGS, type TransitionVariant } from './types';
import { routeVariant } from './routeVariants';

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface TransitionOptions extends NavigateOptions {
  variant?: TransitionVariant;
}

export function useScreenTransition() {
  const navigate = useNavigate();
  const enabled = useAnimationsEnabled();
  const setPhase = useTransitionStore((s) => s.set);

  const tx = useCallback(
    async (to: string, opts: TransitionOptions = {}) => {
      const { variant: variantArg, ...navOpts } = opts;
      const variant = variantArg ?? routeVariant(to);

      if (!enabled) {
        navigate(to, navOpts);
        return;
      }

      const t = VARIANT_TIMINGS[variant];

      setPhase('outgoing', variant);
      await wait(t.out);

      if (t.mid > 0) {
        // Половину middle ждём со старым экраном (он уже opacity 0 после
        // outgoing — но React Router пока его держит), потом навигируем
        // и ждём вторую половину. Чёрный MiddleFrame маскирует jank рендера
        // тяжёлых экранов (BattleScreen, DeckEditorScreen и т.п.).
        setPhase('middle', variant);
        const half = Math.max(1, Math.floor(t.mid / 2));
        await wait(half);
        navigate(to, navOpts);
        await wait(t.mid - half);
      } else {
        navigate(to, navOpts);
      }

      setPhase('incoming', variant);
      await wait(t.in);

      setPhase('idle', variant);
    },
    [navigate, enabled, setPhase]
  );

  return { navigate: tx };
}
